import React from 'react';

import { SortableTable } from '~/components/core';
import Chip from '~/components/core/Atomic/Chip/Chip';
import OverflowTextWithToolTip from '~/components/OverflowTextWithToolTip';
import type { UserModel } from '~/components/types/user-types';
import Checkbox from '~/components/ui/atoms/Checkbox';
import { serverDateTimeToLocal } from '~/DateTimeUtils';
import type { AuditedConfigurationChangeListAuditedConfigurationChange } from '~/server_shared/openapi/data-contracts';
import { prettifyKey } from '~/Utils';

interface AuditLogTableProps {
  rows: AuditedConfigurationChangeListAuditedConfigurationChange[];
  users: UserModel[];
  selectedChangeIds: string[];
  onSelectedChangeIdsChange: (ids: string[]) => void;
  withSelectChanges: boolean;
}

const AuditLogTable: React.FC<AuditLogTableProps> = ({
  rows,
  users,
  selectedChangeIds,
  onSelectedChangeIdsChange,
  withSelectChanges = true,
}) => {
  const columns = [
    {
      id: 'select_checkbox',
      isHidden: !withSelectChanges,
      label: (
        <Checkbox
          disabled={rows.length === 0}
          className="ml-[6px]"
          checked={
            rows.length > 0 && rows.every((row) => row.change_uuid && selectedChangeIds.includes(row.change_uuid))
          }
          onChange={(_event, newValue) => {
            if (newValue) {
              // Select all - only include rows with valid change_uuid
              onSelectedChangeIdsChange(rows.filter((row) => row.change_uuid).map((row) => row.change_uuid!));
            } else {
              // Deselect all
              onSelectedChangeIdsChange([]);
            }
          }}
        />
      ),
      width: 25,
      disableSort: true,
      specialCell: (row: AuditedConfigurationChangeListAuditedConfigurationChange) => (
        <Checkbox
          disabled={!row.change_uuid} // Disable checkbox for rows without change_uuid
          checked={row.change_uuid ? selectedChangeIds.includes(row.change_uuid) : false}
          onChange={(_event, newValue) => {
            if (!row.change_uuid) return; // Early return if no change_uuid

            if (newValue) {
              onSelectedChangeIdsChange([...selectedChangeIds, row.change_uuid]);
            } else {
              onSelectedChangeIdsChange(selectedChangeIds.filter((id) => id !== row.change_uuid));
            }
          }}
        />
      ),
    },
    {
      id: 'applied_at',
      label: 'Applied At',
      specialCell: (row: AuditedConfigurationChangeListAuditedConfigurationChange) => {
        return row.applied_at ? serverDateTimeToLocal(row.applied_at) : '';
      },
    },
    {
      id: 'user_id',
      label: 'User',
      specialCell: (row: AuditedConfigurationChangeListAuditedConfigurationChange) => {
        const user = users?.find((user) => user.id === row.user_id);
        return <Chip label={user ? `${user.first_name} ${user.last_name}` : ''} size="small" />;
      },
    },
    {
      id: 'configuration_type',
      label: 'Config Type',
      width: '70px',
      specialCell: (row: AuditedConfigurationChangeListAuditedConfigurationChange) => {
        return <OverflowTextWithToolTip maxWidth="70px">{prettifyKey(row.configuration_type)}</OverflowTextWithToolTip>;
      },
    },
    {
      id: 'change_type',
      label: 'Change Type',
      specialCell: (row: AuditedConfigurationChangeListAuditedConfigurationChange) => {
        switch (row?.change_type) {
          case 'UPDATE':
            return <span className="rounded-md bg-yellow-100 p-4 font-bold text-yellow-700">UPDATED</span>;
          case 'CREATE':
            return <span className="rounded-md bg-green-100 p-4 font-bold text-green-700">CREATED</span>;
          case 'DELETE':
            return <span className="rounded-md bg-red-100 p-4 font-bold text-red-700">DELETED</span>;
          default:
            return <span className="rounded-md bg-slate-100 p-4 font-bold text-slate-700">{row?.change_type}</span>;
        }
      },
    },
    {
      id: 'field_path',
      label: 'Object',
      specialCell: (row: AuditedConfigurationChangeListAuditedConfigurationChange) => {
        return row?.field_path?.path?.split('.').map(prettifyKey).join(' → ');
      },
    },
    {
      id: 'object_changes',
      label: 'Object Changes',
      specialCell: (row: AuditedConfigurationChangeListAuditedConfigurationChange) => {
        return row?.object_changes?.map((change, idx) => (
          <div key={`${change.desc}-${idx}`} className="my-8 inline-flex items-center">
            <span>{change.desc}</span>
            <span className="pr-4">:</span>
            {row?.change_type === 'UPDATE' ? (
              <>
                <span className="rounded-md bg-red-100 p-4">{JSON.stringify(change.old_value)}</span>
                <span className="px-4">→</span>
                <span className="rounded-md bg-green-100 p-4">{JSON.stringify(change.new_value)}</span>
              </>
            ) : (
              <span className="rounded-md bg-green-100 p-4">{JSON.stringify(change.new_value)}</span>
            )}
          </div>
        ));
      },
    },
    {
      id: 'transaction_id',
      label: 'Change ID',
      width: '60px',
      specialCell: (row: AuditedConfigurationChangeListAuditedConfigurationChange) => {
        return <OverflowTextWithToolTip maxWidth="60px">{row.transaction_id || ''}</OverflowTextWithToolTip>;
      },
    },
  ];

  return (
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    <SortableTable columns={columns} rows={rows} order="desc" defaultOrderColumn={1} />
  );
};

export default AuditLogTable;
